import { useMemo } from 'react';
import { PERSONAL_GRACE_DAYS, CATEGORY_CONFIG, effectivePlan, featureLock, resolveSubscription, bannerFor, daysLeft, type Category, type EntCtx, type Feature, type Lock, type Plan, type SubscriptionSnapshot } from '@dd/shared';
import type { ShopSettingsJson, ShopSettingsRow } from '@/db/types';
import { useLive } from './live';
import { useFlags } from '@/stores/boot';
import { useSession } from '@/stores/session';

export const DEFAULT_SETTINGS: ShopSettingsJson = {
  invoiceSms: true, minSmsAmount: '0', smsLang: 'auto', rounding: false, defaultDueDays: 30, autoReminder: false, weekStart: 6, vendorModule: true, dailySummary: false,
  invoiceFooter: '', printerWidth: 58, lowStockAlerts: true,
};

export function useShopRow(): ShopSettingsRow | null | undefined {
  return useLive((d) => d.settings.toCollection().first().then((r) => r ?? null), []);
}

export function useShop() {
  const row = useShopRow();
  const category = (row?.category ?? 'general') as Category;
  const settings = useMemo<ShopSettingsJson>(() => ({ ...DEFAULT_SETTINGS, ...(row?.settings ?? {}) }), [row]);
  return { row, loaded: row !== undefined, id: row?.id ?? '', name: row?.name ?? '', category, cfg: CATEGORY_CONFIG[category], settings, code: row?.code ?? '', phone: row?.phone ?? null, address: row?.address ?? null, categoryChangesLeft: row?.categoryChangesLeft ?? 1, kind: row?.kind ?? 'business' };
}

const toDate = (s?: string | null): Date | null => (s ? new Date(s) : null);

/** Subscription as the phone knows it, resolved against the clock (time-driven transitions show at once, even offline). */
export function useSubscription() {
  const row = useShopRow();
  return useMemo(() => {
    const s = row?.subscription;
    if (!s) return null;
    const snap: SubscriptionSnapshot = {
      plan: s.plan as Plan, status: s.status, freeReason: s.freeReason as never, trialEndsAt: toDate(s.trialEndsAt), currentPeriodEnd: toDate(s.currentPeriodEnd), graceEndsAt: toDate(s.graceEndsAt),
      cancelAtPeriodEnd: s.cancelAtPeriodEnd, extraShops: s.extraShops, lapsedAt: toDate(s.lapsedAt),
    };
    const now = new Date();
    const cur = resolveSubscription(snap, now);
    return { snap: cur, plan: effectivePlan(cur), status: cur.status, banner: bannerFor(cur, now), daysLeft: daysLeft(cur, now) };
  }, [row]);
}

/** Entitlements (Guide §8.3). `lock(f)` says why a feature is unavailable: 'ok' | 'locked_plan' | 'hidden_flag' | 'suspended'. */
export function useEnt() {
  const sub = useSubscription();
  const flags = useFlags();
  const { category } = useShop();
  const role = useSession((s) => s.profile?.role ?? 'cashier');
  return useMemo(() => {
    const ctx: EntCtx | null = sub ? { account: {}, sub: sub.snap, flags, category, readiness: { ready: true } } : null;
    const lock = (f: Feature): Lock => (ctx ? featureLock(f, ctx) : 'locked_plan');
    return { plan: (sub?.plan ?? 'free') as Plan, status: sub?.status ?? 'free', lock, ok: (f: Feature) => lock(f) === 'ok', role, ready: !!ctx };
  }, [sub, flags, category, role]);
}

export interface PersonalAccessView { isPersonal: boolean; status: 'none' | 'trial' | 'active' | 'past_due' | 'restricted'; source: string; writable: boolean; daysLeft: number | null; until: Date | null }

/**
 * Personal Savings is a separate paid product (Bible 6.8.1): trial → active → grace (3 days) → restricted.
 * Restricted = data is never deleted, view and Excel/PDF export stay open, only NEW entries are blocked. The phone applies the clock itself so it also works offline.
 */
/** The phone applies the clock itself (offline-safe) with the same rules as the server: a trial ends straight into restricted; a paid period gets 3 days of grace. */
export function computePersonalAccess(isPersonal: boolean, p: { status: PersonalAccessView['status']; source: string; writable: boolean; until?: string | null } | null | undefined, nowMs: number): PersonalAccessView {
  if (!isPersonal) return { isPersonal: false, status: 'active', source: 'n/a', writable: true, daysLeft: null, until: null };
  if (!p) return { isPersonal, status: 'active', source: 'unknown', writable: true, daysLeft: null, until: null }; // not synced yet: never lock someone out because of missing data
  if (p.source === 'business_bonus' || p.status === 'none') return { isPersonal, status: p.status, source: p.source, writable: p.writable, daysLeft: null, until: null };
  const until = p.until ? new Date(p.until) : null;
  let status = p.status;
  if (until && nowMs > +until) {
    if (status === 'trial') status = 'restricted';
    else if (status === 'active') status = 'past_due';
  }
  const graceEnd = until ? (p.status === 'past_due' ? +until : +until + PERSONAL_GRACE_DAYS * 86_400_000) : null;
  if (status === 'past_due' && graceEnd !== null && nowMs > graceEnd) status = 'restricted';
  const end = status === 'restricted' ? null : status === 'past_due' ? graceEnd : until ? +until : null;
  return { isPersonal, status, source: status === 'restricted' ? 'restricted' : p.source, writable: status !== 'restricted', daysLeft: end ? Math.max(0, Math.ceil((end - nowMs) / 86_400_000)) : null, until };
}

export function usePersonalAccess(): PersonalAccessView {
  const row = useShopRow();
  return useMemo(() => computePersonalAccess(row?.category === 'personal_savings' || row?.kind === 'personal', row?.personal, Date.now()), [row]);
}
